import { useTranslation } from 'react-i18next';
import type { FormativeActivityDetail } from '../../../modules/tutor/api/malayFormativeProgress.client';
import {
  FormativeLevelOverview,
  FormativeSkillCardsGrid,
} from './FormativeEvidenceSkillCard';
import {
  collectTeacherEvidenceRecords,
  collectSkillObservations,
  evidenceGroupsBySkillKey,
  formativeSkillLevelOverview,
  sortSkillsForTeacherPresentation,
} from './teacherFormativeEvidencePresentation';
import { formativeSkillsWithEvidence } from './teacherMalayLearningActivitiesUtils';

export function FormativeSkillDevelopmentSection({
  activityKey,
  detail,
  showSummary = true,
  testId,
}: {
  activityKey: string;
  detail: FormativeActivityDetail | null | undefined;
  showSummary?: boolean;
  testId?: string;
}) {
  const { t } = useTranslation();
  const skills = sortSkillsForTeacherPresentation(detail?.skills ?? []);
  if (skills.length === 0) return null;

  const tracked = skills.length;
  const withEvidence = formativeSkillsWithEvidence(detail);
  const overview = formativeSkillLevelOverview(skills);
  const observations = collectSkillObservations(detail);
  const evidenceBySkill = evidenceGroupsBySkillKey(collectTeacherEvidenceRecords(detail));
  const knownKeys = new Set(skills.map((skill) => skill.skill_key));
  const orphanSkills = Object.values(evidenceBySkill)
    .filter((group) => !knownKeys.has(group.skillKey))
    .map((group) => ({
      skill_key: group.skillKey,
      current_level: (group.records[0]?.masteryLevel || 'not_enough_evidence') as FormativeActivityDetail['skills'][number]['current_level'],
      valid_observation_count: group.recordCount,
    }));
  const displaySkills = [...skills, ...orphanSkills];

  return (
    <div className="space-y-3" data-testid={testId ?? `formative-skill-development-${activityKey}`}>
      {showSummary ? (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 p-3">
            <p className="text-[11px] font-semibold uppercase text-slate-500">
              {t('dashboard.teacher.analyticsScope.malayLearningSkillsTracked')}
            </p>
            <p className="text-lg font-bold text-slate-900 dark:text-white mt-1 tabular-nums">{tracked}</p>
          </div>
          <div className="rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 p-3">
            <p className="text-[11px] font-semibold uppercase text-slate-500">
              {t('dashboard.teacher.analyticsScope.malayLearningSkillsWithEvidence')}
            </p>
            <p className="text-lg font-bold text-slate-900 dark:text-white mt-1 tabular-nums">
              {withEvidence > 0 ? withEvidence : '—'}
            </p>
          </div>
        </div>
      ) : null}

      <FormativeLevelOverview overview={overview} />

      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
        {t('dashboard.malayFormative.teacherEvidence.skillDevelopment')}
      </h3>
      <FormativeSkillCardsGrid
        skills={displaySkills}
        activityKey={activityKey}
        observations={observations}
        evidenceBySkill={evidenceBySkill}
        testId={`malay-learning-skills-${activityKey}`}
      />
    </div>
  );
}
